import React from 'react';
import { EventGroupHeaderNFL } from './EventGroupHeaderNFL';
import { EventGroupHeaderNBA } from './EventGroupHeaderNBA';
import { EventGroupHeaderMLB } from './EventGroupHeaderMLB';
import { EventGroupHeaderNHL } from './EventGroupHeaderNHL';
import { EventsGroupViewCompetition } from 'src/domains/sportsbook/state/eventsListState/eventsGroup/EventsGroupView';
import { EventsListState } from './EventList.state';
import { checkCompetition } from 'src/domains/sportsbook/utils/checkCompetition';
import { EventsListGroupEvents } from './EventsListGroupEvents';
import { observer } from 'src/utils/mobx-react';
import { CompetitionHeader } from 'src/domains/sportsbook/webview/components/competitionHeader/CompetitionHeader';
import { EventsListCompetitionGroupItemWrapper } from './EventList.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

const emptySelections: string[] = [];

interface EventsListCompetitionHeaderPropsType {
    competitionGroup: EventsGroupViewCompetition;
    headerCompetition: ReturnType<EventsListState['calculateShowFlag']>;
}

const EventsListCompetitionHeader = observer(
    'EventsListCompetitionHeader',
    (props: EventsListCompetitionHeaderPropsType) => {
        const { competitionGroup, headerCompetition } = props;

        const competitionName = competitionGroup.name;
        const competitionSport = competitionGroup.sport;

        const isMLBorMajorCompetition =
            checkCompetition(competitionName, ['MLB', 'Major']) && competitionSport === 'baseball';
        const isNHL = checkCompetition(competitionName, ['NHL']);
        const isNBA = checkCompetition(competitionName, ['NBA']);
        const isNFL = competitionName === 'US NFL' || competitionName === 'NFL Matches';

        if (headerCompetition.showHeader === false) {
            return null;
        }

        if (isMLBorMajorCompetition && competitionGroup.areEventsTwoParticipantsOnly) {
            return (
                <EventGroupHeaderMLB
                    dataTest='header-competitionLevel'
                    label={competitionName}
                    onToggle={competitionGroup.toggle}
                    open={headerCompetition.showBody}
                />
            );
        }
        if (isNHL && competitionGroup.areEventsTwoParticipantsOnly) {
            return (
                <EventGroupHeaderNHL
                    dataTest='header-competitionLevel'
                    label={competitionName}
                    onToggle={competitionGroup.toggle}
                    open={headerCompetition.showBody}
                />
            );
        }
        if (isNBA && competitionGroup.areEventsTwoParticipantsOnly) {
            return (
                <EventGroupHeaderNBA
                    dataTest='header-competitionLevel'
                    label={competitionName}
                    onToggle={competitionGroup.toggle}
                    open={headerCompetition.showBody}
                />
            );
        }
        if (isNFL) {
            return (
                <EventGroupHeaderNFL
                    dataTest='header-competitionLevel'
                    label={competitionName}
                    onToggle={competitionGroup.toggle}
                    open={headerCompetition.showBody}
                />
            );
        }
        return (
            <CompetitionHeader
                dataTest='header-competitionLevel'
                isOpen={headerCompetition.showBody}
                isOutrightEvent={headerCompetition.showBody ? competitionGroup.isOutrightEvent : false}
                label={competitionName}
                onToggle={competitionGroup.toggle}
                selections={headerCompetition.showBody ? competitionGroup.lastSelectionsHeader : emptySelections}
            />
        );
    }
);

interface EventsListCompetitionBodyPropsType {
    competitionGroup: EventsGroupViewCompetition;
    filterMarketsByPopularTag: boolean;
    show: boolean;
}

const EventsListCompetitionBody = observer('EventsListCompetitionBody', (props: EventsListCompetitionBodyPropsType) => {
    const { competitionGroup, filterMarketsByPopularTag, show } = props;

    if (show === false) {
        return null;
    }

    return (
        <EventsListGroupEvents
            filterMarketsByPopularTag={filterMarketsByPopularTag}
            competitionGroup={competitionGroup}
        />
    );
});

interface EventsListCompetitionGroupItemPropsType {
    competitionGroup: EventsGroupViewCompetition;
    filterMarketsByPopularTag: boolean;
    state: EventsListState;
}

export const EventsListCompetitionGroupItem = observer(
    'EventsListCompetitionGroupItem',
    (props: EventsListCompetitionGroupItemPropsType) => {
        const { competitionGroup, filterMarketsByPopularTag, state } = props;
        const common = useCommon();
        const configState = ConfigComponents.get(common).config;

        const headerCompetition = state.calculateShowFlag(true, competitionGroup.visible);

        const headerEl: JSX.Element | null = (
            <EventsListCompetitionHeader
                competitionGroup={competitionGroup}
                headerCompetition={headerCompetition}
            />
        );

        const bodyEl: JSX.Element | null = (
            <EventsListCompetitionBody
                competitionGroup={competitionGroup}
                filterMarketsByPopularTag={filterMarketsByPopularTag}
                show={headerCompetition.showBody}
            />
        );

        return (
            <EventsListCompetitionGroupItemWrapper variant={configState.componentVariants.sportsbookVariant}>
                {headerEl}
                {bodyEl}
            </EventsListCompetitionGroupItemWrapper>
        );
    }
);
